import { Injectable, signal } from '@angular/core';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { forkJoin, Observable, of, Subscription } from 'rxjs';
import { catchError, finalize, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { ProductConfigService } from '../../../core/services/product-config.service';
import { DenefitsUnpaidApiService } from './children/denefits/denefits-unpaid-api.service';
import {
  EMPTY_DENEFITS_BALANCE,
  getVersionDateRange,
} from './children/denefits/denefits-unpaid.config';
import {
  PartnerOverviewData,
  UnpaidPartnerTileSummaryService,
} from './unpaid-partner-tile-summary.service';
import {
  UnifiedUnpaidFilters,
  UnpaidPaymentsAdapterService,
} from './unpaid-payments-adapter.service';
import { UnpaidPaymentsAccessService } from './unpaid-payments-access.service';
import { isUnpaidProductAuthorized } from './unpaid-payments-access.util';
import { UnpaidPaymentsIsDummyService } from './unpaid-payments-is-dummy.service';
import {
  UNPAID_HUB_PRODUCTS,
  UnpaidHubProductDefinition,
  getDefaultFmFilter,
} from './unpaid-payments-hub-products.config';
import { isUnpaidPaymentsProductRouteAvailable } from './unpaid-payments-route.config';

dayjs.extend(utc);
dayjs.extend(timezone);

const LA_TIMEZONE = 'America/Los_Angeles';
const FULL_LIMIT_OVERVIEW_PAGE_SIZE = 10;
const FULL_LIMIT_OVERVIEW_PRODUCTS = new Set(['']); // with current filters and response from the products, we don't need to fetch all the data, for future use `['recuvery', 'fm']`
const OVERVIEW_CACHE_TTL_MS = 10 * 60 * 1000;
const OVERVIEW_REFRESH_INTERVAL_MS = 10 * 60 * 1000;
const SESSION_OVERVIEW_CACHE_PREFIX = 'unpaid_hub_overview_v1';
const laTodayYmd = (): string => dayjs().tz(LA_TIMEZONE).format('YYYY-MM-DD');

interface CachedOverviewCard {
  expiresAt: number;
  card: UnpaidHubProductCardState;
}

interface SessionOverviewCacheEntry {
  expiresAt: number;
  data: PartnerOverviewData;
}

export interface UnpaidHubProductCardState {
  product: UnpaidHubProductDefinition;
  loading: boolean;
  data: PartnerOverviewData;
  unauthorized?: boolean;
}

export { UNPAID_HUB_PRODUCTS } from './unpaid-payments-hub-products.config';
export type { UnpaidHubProductDefinition } from './unpaid-payments-hub-products.config';

const EMPTY_OVERVIEW: PartnerOverviewData = {
  availableBalance: 0,
  upcomingAmount: 0,
  totalPaymentToPay: 0,
  totalPaymentsCount: 0,
  urgentPaymentsAmount: 0,
  urgentPaymentsCount: 0,
};

function pickValue(root: unknown, keyPath: string): unknown {
  if (root === null || root === undefined) {
    return undefined;
  }
  const segments = keyPath.split('.').map((s) => s.trim()).filter(Boolean);
  let cur: unknown = root;
  for (const seg of segments) {
    if (cur === null || cur === undefined || typeof cur !== 'object') {
      return undefined;
    }
    cur = (cur as Record<string, unknown>)[seg];
  }
  return cur;
}

function pickNumber(root: unknown, keyPaths: string[], allowZero = false): number | null {
  for (const keyPath of keyPaths) {
    const raw = pickValue(root, keyPath);
    if (raw === undefined || raw === null || raw === '') {
      continue;
    }
    const n = Number(raw);
    if (Number.isFinite(n) && (allowZero || n !== 0)) {
      return n;
    }
  }
  return null;
}

@Injectable({
  providedIn: 'root',
})
export class UnpaidPaymentsHubOverviewService {
  readonly cards = signal<UnpaidHubProductCardState[]>(
    UNPAID_HUB_PRODUCTS.map((product) => ({
      product,
      loading: true,
      data: EMPTY_OVERVIEW,
    }))
  );

  private readonly overviewCardCache = new Map<string, CachedOverviewCard>();
  private readonly overviewCardInflight = new Map<string, Observable<UnpaidHubProductCardState>>();
  private activeCardsSubscription: Subscription | null = null;
  private refreshIntervalId: ReturnType<typeof setInterval> | null = null;

  constructor(
    private readonly adapterService: UnpaidPaymentsAdapterService,
    private readonly productConfigService: ProductConfigService,
    private readonly partnerTileSummary: UnpaidPartnerTileSummaryService,
    private readonly denefitsApi: DenefitsUnpaidApiService,
    private readonly isDummyService: UnpaidPaymentsIsDummyService,
    private readonly accessService: UnpaidPaymentsAccessService
  ) {}

  startAutoRefresh(): void {
    if (this.refreshIntervalId !== null) {
      return;
    }

    this.refreshIntervalId = setInterval(
      () => this.loadCards({ forceRefresh: true }),
      OVERVIEW_REFRESH_INTERVAL_MS
    );
  }

  stopAutoRefresh(): void {
    if (this.refreshIntervalId !== null) {
      clearInterval(this.refreshIntervalId);
      this.refreshIntervalId = null;
    }

    this.activeCardsSubscription?.unsubscribe();
    this.activeCardsSubscription = null;
  }

  loadCards(options: { forceRefresh?: boolean } = {}): void {
    if (!options.forceRefresh) {
      this.prepareOverviewCards();
    }

    if (this.activeCardsSubscription && !this.activeCardsSubscription.closed) {
      return;
    }

    const productsToFetch = (options.forceRefresh
      ? UNPAID_HUB_PRODUCTS
      : UNPAID_HUB_PRODUCTS.filter((product) => this.shouldFetchProduct(product))
    ).filter((product) => isUnpaidPaymentsProductRouteAvailable(product.key));

    if (!productsToFetch.length) {
      return;
    }

    this.activeCardsSubscription = forkJoin(
      productsToFetch.map((product) => this.loadProductCard(product, options))
    ).subscribe();
  }

  /** Hydrate overview cards from session/memory cache and in-memory product page summaries. */
  prepareOverviewCards(): void {
    for (const product of UNPAID_HUB_PRODUCTS) {
      if (this.accessService.isProbed(product.key) && !this.accessService.hasAccess(product.key)) {
        this.applyCachedAccessToCard(product);
        continue;
      }

      const cached = this.readCachedCard(product);
      if (cached) {
        this.updateCardState(cached);
        continue;
      }

      this.syncCardFromPartnerSummary(product);
    }
  }

  /** Loads hub overview card data; cached in memory and sessionStorage for {@link OVERVIEW_CACHE_TTL_MS}. */
  loadProductCard(
    product: UnpaidHubProductDefinition,
    options: { forceRefresh?: boolean } = {}
  ): Observable<UnpaidHubProductCardState> {
    if (!options.forceRefresh) {
      if (this.accessService.isProbed(product.key) && !this.accessService.hasAccess(product.key)) {
        const deniedCard: UnpaidHubProductCardState = {
          product,
          loading: false,
          unauthorized: true,
          data: EMPTY_OVERVIEW,
        };
        return this.watchCardState(of(deniedCard));
      }

      const cached = this.readCachedCard(product);
      if (cached) {
        return this.watchCardState(of(cached));
      }
    }

    const inflight = this.overviewCardInflight.get(product.key);
    if (inflight) {
      return this.watchCardState(inflight);
    }

    const request$ = this.fetchProductCard(product).pipe(
      finalize(() => this.overviewCardInflight.delete(product.key)),
      shareReplay({ bufferSize: 1, refCount: true })
    );
    this.overviewCardInflight.set(product.key, request$);
    return this.watchCardState(request$);
  }

  private watchCardState(
    source$: Observable<UnpaidHubProductCardState>
  ): Observable<UnpaidHubProductCardState> {
    return source$.pipe(tap((card) => this.updateCardState(card)));
  }

  private shouldFetchProduct(product: UnpaidHubProductDefinition): boolean {
    if (this.readCachedCard(product)) {
      return false;
    }

    if (this.accessService.isProbed(product.key) && !this.accessService.hasAccess(product.key)) {
      this.applyCachedAccessToCard(product);
      return false;
    }

    const current = this.cards().find((card) => card.product.key === product.key);
    if (current && !current.loading && !current.unauthorized) {
      return false;
    }

    if (this.accessService.hasAccess(product.key) && this.hasMeaningfulOverview(this.partnerTileSummary.overviewFor(product.key))) {
      this.syncCardFromPartnerSummary(product);
      return false;
    }

    return true;
  }

  /** Apply session-cached access denial without calling product APIs. */
  private applyCachedAccessToCard(product: UnpaidHubProductDefinition): boolean {
    if (!this.accessService.isProbed(product.key) || this.accessService.hasAccess(product.key)) {
      return false;
    }

    const card: UnpaidHubProductCardState = {
      product,
      loading: false,
      unauthorized: true,
      data: EMPTY_OVERVIEW,
    };
    this.updateCardState(card);
    this.overviewCardCache.set(product.key, {
      expiresAt: Date.now() + OVERVIEW_CACHE_TTL_MS,
      card,
    });
    return true;
  }

  /** Called from product pages after a successful list load to persist overview tiles in cache. */
  cacheProductOverviewFromPage(productKey: string): void {
    const product = UNPAID_HUB_PRODUCTS.find((item) => item.key === productKey);
    if (!product) {
      return;
    }
    this.syncCardFromPartnerSummary(product);
  }

  private hasMeaningfulOverview(overview: PartnerOverviewData): boolean {
    return (
      overview.availableBalance !== 0 ||
      overview.upcomingAmount !== 0 ||
      overview.totalPaymentToPay !== 0 ||
      overview.totalPaymentsCount !== 0 ||
      overview.urgentPaymentsAmount !== 0 ||
      overview.urgentPaymentsCount !== 0
    );
  }

  /** Promote product-page overview data into hub cards + session cache when access is granted. */
  syncCardFromPartnerSummary(product: UnpaidHubProductDefinition): boolean {
    if (!this.accessService.hasAccess(product.key)) {
      return false;
    }

    const overview = this.partnerTileSummary.overviewFor(product.key);
    if (!this.hasMeaningfulOverview(overview)) {
      return false;
    }

    const card: UnpaidHubProductCardState = {
      product,
      loading: false,
      data: overview,
    };
    this.updateCardState(card);
    this.writeCachedCard(product, card);
    return true;
  }

  private updateCardState(card: UnpaidHubProductCardState): void {
    this.cards.update((cards) =>
      cards.map((current) => (current.product.key === card.product.key ? card : current))
    );
  }

  private fetchProductCard(product: UnpaidHubProductDefinition): Observable<UnpaidHubProductCardState> {
    return this.loadProduct(product).pipe(
      map(({ overview, accessSource }) => {
        this.accessService.evaluate(product.key, accessSource);
        if (!this.accessService.hasAccess(product.key)) {
          return {
            product,
            loading: false,
            unauthorized: true,
            data: EMPTY_OVERVIEW,
          };
        }
        return {
          product,
          loading: false,
          data: overview,
        };
      }),
      tap((card) => {
        this.writeCachedCard(product, card);
      }),
      catchError(() => {
        this.accessService.markUnauthorized(product.key);
        return of({
          product,
          loading: false,
          unauthorized: true,
          data: EMPTY_OVERVIEW,
        });
      })
    );
  }

  private readCachedCard(product: UnpaidHubProductDefinition): UnpaidHubProductCardState | null {
    const now = Date.now();
    const memory = this.overviewCardCache.get(product.key);
    if (memory && now < memory.expiresAt) {
      return memory.card;
    }

    const session = this.readSessionCache(product.key);
    if (!session || now >= session.expiresAt) {
      return null;
    }

    const card: UnpaidHubProductCardState = {
      product,
      loading: false,
      data: session.data,
    };
    this.partnerTileSummary.patchOverview(product.key, session.data);
    this.overviewCardCache.set(product.key, { expiresAt: session.expiresAt, card });
    return card;
  }

  private writeCachedCard(product: UnpaidHubProductDefinition, card: UnpaidHubProductCardState): void {
    const entry: CachedOverviewCard = {
      expiresAt: Date.now() + OVERVIEW_CACHE_TTL_MS,
      card,
    };
    this.overviewCardCache.set(product.key, entry);
    if (!card.unauthorized) {
      this.writeSessionCache(product.key, entry.expiresAt, card.data);
    }
  }

  private sessionCacheKey(productKey: string): string {
    return `${SESSION_OVERVIEW_CACHE_PREFIX}:${this.isDummyService.getStorageValue()}:${productKey}`;
  }

  private readSessionCache(productKey: string): SessionOverviewCacheEntry | null {
    if (typeof sessionStorage === 'undefined') {
      return null;
    }
    try {
      const raw = sessionStorage.getItem(this.sessionCacheKey(productKey));
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw) as SessionOverviewCacheEntry;
      if (
        !parsed ||
        typeof parsed.expiresAt !== 'number' ||
        !parsed.data ||
        typeof parsed.data !== 'object'
      ) {
        sessionStorage.removeItem(this.sessionCacheKey(productKey));
        return null;
      }
      if (Date.now() >= parsed.expiresAt) {
        sessionStorage.removeItem(this.sessionCacheKey(productKey));
        return null;
      }
      return parsed;
    } catch {
      sessionStorage.removeItem(this.sessionCacheKey(productKey));
      return null;
    }
  }

  private writeSessionCache(
    productKey: string,
    expiresAt: number,
    data: PartnerOverviewData
  ): void {
    if (typeof sessionStorage === 'undefined') {
      return;
    }
    try {
      const payload: SessionOverviewCacheEntry = { expiresAt, data };
      sessionStorage.setItem(this.sessionCacheKey(productKey), JSON.stringify(payload));
    } catch {
      // Quota exceeded or private mode — in-memory cache still applies for this session.
    }
  }

  private loadProduct(
    product: UnpaidHubProductDefinition
  ): Observable<{ overview: PartnerOverviewData; accessSource: unknown }> {
    if (product.key === 'denefits') {
      return this.loadDenefitsOverview();
    }
    return this.loadAdapterOverview(product.key);
  }

  private loadDenefitsOverview(): Observable<{ overview: PartnerOverviewData; accessSource: unknown }> {
    const { startDate, endDate } = getVersionDateRange(0);

    return forkJoin({
      balance: this.denefitsApi.getBalance().pipe(catchError(() => of(null))),
      dashboard: this.denefitsApi.getDashboard(startDate, endDate).pipe(catchError(() => of(null))),
    }).pipe(
      map(({ balance, dashboard }) => {
        const accessSource = dashboard ?? balance;
        let snapshot = { ...EMPTY_DENEFITS_BALANCE };

        for (const source of [
          balance?.data?.balance_obj,
          balance?.balance_obj,
          dashboard?.data?.balance_obj,
          dashboard?.balance_obj,
        ]) {
          snapshot = this.denefitsApi.mergeBalance(snapshot, source);
        }

        const dashboardTotal = this.sumDenefitsDashboardAmount(dashboard);
        const totalAmountToPay =
          snapshot.total_amount_to_pay > 0
            ? snapshot.total_amount_to_pay
            : dashboardTotal > 0
              ? dashboardTotal
              : pickNumber(balance, ['data.total_amount_to_pay', 'total_amount_to_pay'], true) ??
                pickNumber(dashboard, ['data.total_amount_to_pay', 'total_amount_to_pay'], true) ??
                0;

        const overview: PartnerOverviewData = {
          availableBalance: snapshot.available_balance,
          upcomingAmount: snapshot.upcoming_balance,
          totalPaymentToPay: totalAmountToPay,
          totalPaymentsCount: 0,
          urgentPaymentsAmount: snapshot.urgent_balance,
          urgentPaymentsCount: snapshot.total_urgent_payments,
        };
        this.partnerTileSummary.patchOverview('denefits', overview);
        return { overview, accessSource };
      })
    );
  }

  private sumDenefitsDashboardAmount(response: unknown): number {
    const listObject = pickValue(response, 'data.list_obj');
    if (!listObject || typeof listObject !== 'object') {
      return 0;
    }

    let total = 0;
    for (const section of Object.values(listObject as Record<string, unknown>)) {
      if (!section || typeof section !== 'object') {
        continue;
      }
      for (const item of Object.values(section as Record<string, unknown>)) {
        total += Number(pickValue(item, 'total_amount_to_pay') ?? 0) || 0;
      }
    }
    return total;
  }

  private loadAdapterOverview(
    productKey: string
  ): Observable<{ overview: PartnerOverviewData; accessSource: unknown }> {
    const product = this.adapterService.getProducts().find((p) => p.key === productKey);
    if (!product) {
      return of({ overview: EMPTY_OVERVIEW, accessSource: null });
    }

    const config = this.productConfigService.getProductConfig(product.serverName);
    if (!config?.productServerName) {
      return of({ overview: EMPTY_OVERVIEW, accessSource: null });
    }

    const filters = this.defaultListFilters(productKey);
    const firstPage$ = this.adapterService.listPayments(productKey, config.productServerName, filters);

    if ( !FULL_LIMIT_OVERVIEW_PRODUCTS.has(productKey)) {
      return firstPage$.pipe(
        switchMap((res) => {
          if (!isUnpaidProductAuthorized(res)) {
            this.markUnauthorizedFromFirstResponse(productKey, res);
            return of({ overview: EMPTY_OVERVIEW, accessSource: res });
          }
          return of({
            overview: this.mapListResponseToOverview(productKey, res),
            accessSource: res,
          });
        })
      );
    }

    return firstPage$.pipe(
      switchMap((firstPageRes) => {
        if (!isUnpaidProductAuthorized(firstPageRes)) {
          this.markUnauthorizedFromFirstResponse(productKey, firstPageRes);
          return of({ overview: EMPTY_OVERVIEW, accessSource: firstPageRes });
        }

        const totalRecords =
          pickNumber(
            firstPageRes,
            ['pagination.total', 'data.pagination.total', 'count', 'data.count', 'number_of_records', 'total_records'],
            true
          ) ?? 0;
        const initialLimit = Number(filters.limit ?? FULL_LIMIT_OVERVIEW_PAGE_SIZE);
        const fullLimit = Math.max(initialLimit, totalRecords);

        if (fullLimit <= initialLimit) {
          return of({
            overview: this.mapListResponseToOverview(productKey, firstPageRes),
            accessSource: firstPageRes,
          });
        }

        return this.adapterService
          .listPayments(productKey, config.productServerName, {
            ...filters,
            limit: fullLimit,
            offset: 0,
          })
          .pipe(
            catchError(() => of(firstPageRes)),
            map((res) => ({
              overview: this.mapListResponseToOverview(productKey, res),
              accessSource: res,
            }))
          );
      })
    );
  }

  private markUnauthorizedFromFirstResponse(productKey: string, accessSource: unknown): void {
    if (isUnpaidProductAuthorized(accessSource)) {
      return;
    }

    this.accessService.evaluate(productKey, accessSource);

    const product = UNPAID_HUB_PRODUCTS.find((item) => item.key === productKey);
    if (!product) {
      return;
    }

    this.updateCardState({
      product,
      loading: false,
      unauthorized: true,
      data: EMPTY_OVERVIEW,
    });
  }

  private mapListResponseToOverview(productKey: string, res: unknown): PartnerOverviewData {
    this.partnerTileSummary.updateFromListResponse(productKey, res);
    const parsed = this.partnerTileSummary.overviewFor(productKey);

    const overview: PartnerOverviewData = {
      availableBalance:
        pickNumber(res, ['available_balance', 'availableBalance'], true) ?? parsed.availableBalance,
      upcomingAmount: this.resolveUpcomingAmount(productKey, res, parsed.upcomingAmount),
      totalPaymentToPay: this.resolveAmountToPay(productKey, res, parsed.totalPaymentToPay),
      totalPaymentsCount: parsed.totalPaymentsCount,
      urgentPaymentsAmount:
        pickNumber(res, ['urgent_balance', 'urgent_amount', 'total_urgent_amount'], true) ??
        parsed.urgentPaymentsAmount,
      urgentPaymentsCount:
        pickNumber(res, ['total_urgent_payments', 'urgent_count'], true) ?? parsed.urgentPaymentsCount,
    };

    this.partnerTileSummary.patchOverview(productKey, overview);
    return overview;
  }

  private resolveUpcomingAmount(productKey: string, res: unknown, fallback: number): number {
    const upcoming =
      pickNumber(res, ['upcoming_balance', 'upcomingBalance', 'pending_balance', 'pendingBalance'], true) ??
      fallback;

    if (productKey === 'lendee' && upcoming === 0) {
      return pickNumber(res, ['platform_balance', 'platformBalance'], true) ?? upcoming;
    }

    return upcoming;
  }

  private resolveAmountToPay(productKey: string, res: unknown, fallback: number): number {
    if (productKey === 'lendee') {
      const releasedAmount = pickNumber(
        res,
        [
          'released_payment_amount',
          'released_payments_amount',
          'payable_amount',
          'total_payable_amount',
          'released_pay_to_user',
          'released_pay_to_users',
          'uspa_ids.released_payment_amount',
          'uspa_ids.released_amount',
          'uspa_ids.released_pay_to_user',
          'uspaIds.released_payment_amount',
          'uspaIds.released_amount',
          'uspaIds.released_pay_to_user',
        ],
        true
      );
      if (releasedAmount !== null) {
        return releasedAmount;
      }

      const payToUsers = pickNumber(
        res,
        ['total_pay_to_users', 'total_pay_to_user', 'uspa_ids.total_pay_to_user', 'uspaIds.total_pay_to_user'],
        true
      );
      if (payToUsers !== null) {
        return payToUsers;
      }

      const totalAmount = pickNumber(
        res,
        ['total_amount', 'totalAmount', 'total_amount_to_pay', 'payment_due_total'],
        true
      );
      const platformBalance =
        pickNumber(res, ['platform_balance', 'platformBalance'], true) ?? 0;
      if (totalAmount !== null && totalAmount > platformBalance) {
        return totalAmount - platformBalance;
      }
      if (totalAmount !== null) {
        return totalAmount;
      }

      return fallback;
    }

    const amount = pickNumber(
      res,
      ['total_amount', 'totalAmount', 'total_amount_to_pay', 'payment_due_total'],
      true
    );
    return amount ?? fallback;
  }

  private defaultListFilters(productKey: string): UnifiedUnpaidFilters {
    if (productKey === 'lendee') {
      return {
        search_string: '',
        limit: 1,
        offset: 0,
        sub_status: -1,
        start_date: '2025-01-01',
        end_date: laTodayYmd(),
      };
    }

    const fmDefaults: UnifiedUnpaidFilters = {
      limit: FULL_LIMIT_OVERVIEW_PRODUCTS.has(productKey) ? FULL_LIMIT_OVERVIEW_PAGE_SIZE : 1,
      offset: 0,
      fm_filter: getDefaultFmFilter(productKey),
      fm_status_filter: '0',
      fm_partner_status: '0',
      fm_orp_filter: -1,
      fm_instant_claim: -1,
      fm_share_type: 'all',
      fm_payment_status: 'all',
      challenge_type: '0',
      is_vip: 0,
      is_urgent: '0',
      start_date: '2025-01-01',
      end_date: laTodayYmd(),
    };

    return fmDefaults;
  }
}
